import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router';
import type { ProjectSummary } from '@dashboard/contracts';
import { useSession } from '../auth/session-context';
import { EmptyState } from '../components/ui/states';

/**
 * The project every project-scoped screen is about.
 *
 * This replaces what used to be a project picker repeated on the Schedule
 * page, the Costs page and the settings panel, each with its own list request,
 * its own "pick the first one" effect and its own loading, error and empty
 * states -- four copies of one question that a deployment answers once. The
 * server resolves it and ships it in the session context, so a page reads it
 * synchronously from state the shell already had before it rendered.
 *
 * Product shape, not a shortcut: the dashboard monitors one infrastructure
 * project for the people working on it (architecture documentation section 2).
 * When a portfolio view is wanted, this is the file that learns to hold a
 * selection -- the pages below it already take the project as a value rather
 * than going to find one, so they do not change.
 */

/** The workspace's project, or null when the organization has none yet. */
export function useOptionalProject(): ProjectSummary | null {
  return useSession().session.project;
}

/**
 * The workspace's project, guaranteed.
 *
 * Only legal below `ProjectGate`, which is what makes the guarantee true. A
 * page that reads this has no "no project" branch to write, and cannot render
 * half a screen against an undefined identifier.
 */
export function useProject(): ProjectSummary {
  const project = useOptionalProject();
  if (!project) {
    throw new Error('useProject requires a route nested under ProjectGate');
  }
  return project;
}

/**
 * Route element for the screens that mean nothing without a project.
 *
 * One statement of the precondition for all of them, in the router where the
 * grouping is visible, rather than the same early return at the top of each
 * page. A deployment with no project yet gets one honest explanation instead
 * of an empty schedule, an empty cost report and an empty settings panel.
 */
export function ProjectGate() {
  const { t } = useTranslation();
  const project = useOptionalProject();

  if (!project) {
    return <EmptyState title={t('project.noneTitle')} body={t('project.noneBody')} />;
  }

  return <Outlet />;
}
